import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API } from '../App';

const CATEGORIES = [
  { id: 'all',        label: 'Alle',        icon: '📁', color: '#607060' },
  { id: 'planning',   label: 'Planung',     icon: '📋', color: '#3b82f6' },
  { id: 'contract',   label: 'Verträge',    icon: '📄', color: '#8b5cf6' },
  { id: 'compliance', label: 'Compliance',  icon: '✅', color: '#22c55e' },
  { id: 'finance',    label: 'Finanzen',    icon: '💰', color: '#f59e0b' },
  { id: 'template',   label: 'Vorlagen',    icon: '📝', color: '#ec4899' },
  { id: 'other',      label: 'Sonstiges',   icon: '📂', color: '#607060' },
];

const CAT = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));
function catMeta(id) { return CAT[id] || CAT.other; }

function CatBadge({ category }) {
  const m = catMeta(category);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: m.color + '22', color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
}

function fmt_size(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmt_date(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fileIcon(ct) {
  if (!ct) return '📄';
  if (ct.includes('pdf'))   return '📕';
  if (ct.includes('word') || ct.includes('document')) return '📘';
  if (ct.includes('excel') || ct.includes('sheet'))   return '📗';
  if (ct.includes('presentation') || ct.includes('powerpoint')) return '📙';
  if (ct.includes('image')) return '🖼️';
  if (ct.includes('html'))  return '🌐';
  if (ct.includes('csv') || ct.includes('text')) return '📃';
  if (ct.includes('zip'))   return '🗜️';
  return '📄';
}

function isPreviewable(ct) {
  if (!ct) return false;
  return ct.includes('pdf') || ct.includes('image') || ct.includes('html') || ct.includes('text');
}

// ── Upload Modal ──────────────────────────────────────────────────────────────
function UploadModal({ onClose, onUploaded }) {
  const [files, setFiles]      = useState([]);
  const [title, setTitle]      = useState('');
  const [description, setDesc] = useState('');
  const [category, setCat]     = useState('planning');
  const [loading, setLoading]  = useState(false);
  const [err, setErr]          = useState('');
  const [drag, setDrag]        = useState(false);
  const inputRef = useRef();

  const handleDrop = (e) => { e.preventDefault(); setDrag(false); setFiles(Array.from(e.dataTransfer.files)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) { setErr('Bitte Datei auswählen'); return; }
    setLoading(true); setErr('');
    let uploaded = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title || file.name);
      fd.append('description', description);
      fd.append('category', category);
      try { await API.post('/documents', fd); uploaded++; }
      catch (e) { setErr(`Fehler bei "${file.name}": ${e.response?.data?.detail || e.message}`); }
    }
    setLoading(false);
    if (uploaded > 0) { onUploaded(); onClose(); }
  };

  const inSt = { width: '100%', padding: '8px 12px', borderRadius: 7,
    background: 'var(--bg-surface)', border: '1px solid var(--border)',
    color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000080',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: '100%', maxWidth: 520 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text1)', marginBottom: 20 }}>
          📤 Dokument hochladen
        </div>
        {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
          borderRadius: 7, color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{err}</div>}
        <form onSubmit={handleSubmit}>
          <div onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)} onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            style={{ border: `2px dashed ${drag ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 10, padding: 28, textAlign: 'center', cursor: 'pointer',
              background: drag ? 'var(--accent-glow)' : 'var(--bg-surface)',
              transition: 'all .15s', marginBottom: 16 }}>
            <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => setFiles(Array.from(e.target.files))} />
            <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
            {files.length === 0 ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>Dateien hierher ziehen oder klicken</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>PDF, Word, Excel, Bilder, HTML — max. 50 MB</div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
                {files.length === 1 ? files[0].name : `${files.length} Dateien ausgewählt`}
              </div>
            )}
          </div>
          {files.length <= 1 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Titel</label>
              <input type="text" value={title} placeholder={files[0]?.name || 'Dokumententitel'}
                onChange={e => setTitle(e.target.value)} style={inSt} />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Kategorie</label>
            <select value={category} onChange={e => setCat(e.target.value)} style={inSt}>
              {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Beschreibung (optional)</label>
            <textarea value={description} onChange={e => setDesc(e.target.value)}
              rows={2} placeholder="Kurze Beschreibung…" style={{ ...inSt, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text2)',
                border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading || !files.length}
              style={{ padding: '8px 18px', background: 'var(--accent)', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? .7 : 1 }}>
              {loading ? 'Lade hoch…' : `📤 ${files.length > 1 ? `${files.length} Dateien` : 'Hochladen'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ doc, onClose, onSaved }) {
  const [form, setForm]    = useState({ title: doc.title, description: doc.description || '', category: doc.category });
  const [loading, setLoad] = useState(false);
  const [err, setErr]      = useState('');

  const handle = async (e) => {
    e.preventDefault(); setLoad(true); setErr('');
    try { await API.put(`/documents/${doc.id}`, form); onSaved(); onClose(); }
    catch (e) { setErr(e.response?.data?.detail || 'Fehler'); }
    finally { setLoad(false); }
  };

  const inSt = { width: '100%', padding: '8px 12px', borderRadius: 7,
    background: 'var(--bg-surface)', border: '1px solid var(--border)',
    color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000080',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: '100%', maxWidth: 460 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text1)', marginBottom: 16 }}>Dokument bearbeiten</div>
        {err && <div style={{ padding: '8px 12px', background: '#ef444415', borderRadius: 7, color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <form onSubmit={handle}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Titel</label>
            <input type="text" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inSt} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Kategorie</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inSt}>
              {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Beschreibung</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3} style={{ ...inSt, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 14px', background: 'transparent', color: 'var(--text2)',
                border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '8px 16px', background: 'var(--accent)', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {loading ? '…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Document Preview Panel ────────────────────────────────────────────────────
function PreviewPanel({ doc, onClose, onDownload, onEdit, onDelete }) {
  const [blobUrl, setBlobUrl]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const prevDocId = useRef(null);

  const fetchBlob = useCallback(async () => {
    if (!doc) return;
    // Don't re-fetch if same doc
    if (prevDocId.current === doc.id && blobUrl) { setLoading(false); return; }
    prevDocId.current = doc.id;
    setLoading(true); setError(''); setBlobUrl(null);
    try {
      const token = JSON.parse(localStorage.getItem('energy_auth') || 'null')?.access_token || '';
      const resp = await fetch(`/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      setBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError('Vorschau konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [doc]);

  useEffect(() => {
    fetchBlob();
    return () => {
      // Revoke previous blob URL on doc change
    };
  }, [fetchBlob]);

  // Cleanup blob URL when panel unmounts
  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  if (!doc) return null;

  const ct = doc.content_type || '';
  const isPdf   = ct.includes('pdf');
  const isImage = ct.includes('image');
  const isHtml  = ct.includes('html');
  const canPreview = isPdf || isImage || isHtml;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {/* Panel header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>{fileIcon(ct)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text1)',
              wordBreak: 'break-word', lineHeight: 1.3, marginBottom: 4 }}>
              {doc.title}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <CatBadge category={doc.category} />
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{fmt_size(doc.file_size)}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>📅 {fmt_date(doc.created_at)}</span>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 18, color: 'var(--text3)', padding: '2px 6px', flexShrink: 0,
              borderRadius: 4, lineHeight: 1 }}
            title="Vorschau schließen">
            ✕
          </button>
        </div>
        {doc.description && (
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 8, lineHeight: 1.5 }}>
            {doc.description}
          </div>
        )}
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button onClick={() => onDownload(doc)}
            style={{ padding: '5px 12px', background: '#3b82f620', color: '#3b82f6',
              border: '1px solid #3b82f640', borderRadius: 6, cursor: 'pointer',
              fontSize: 12, fontWeight: 600 }}>
            ⬇ Download
          </button>
          <button onClick={() => onEdit(doc)}
            style={{ padding: '5px 10px', background: 'var(--bg-hover)',
              color: 'var(--text2)', border: '1px solid var(--border)',
              borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            ✏️ Bearbeiten
          </button>
          <button onClick={() => onDelete(doc)}
            style={{ padding: '5px 10px', background: '#ef444415', color: '#ef4444',
              border: '1px solid #ef444430', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            🗑
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 0 }}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--text3)' }}>
            <div style={{ fontSize: 32 }}>⏳</div>
            <div style={{ fontSize: 13 }}>Lade Vorschau…</div>
          </div>
        )}

        {error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--text3)',
            padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 40 }}>⚠️</div>
            <div style={{ fontSize: 13 }}>{error}</div>
            <button onClick={() => onDownload(doc)}
              style={{ padding: '8px 16px', background: 'var(--accent)', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              ⬇ Stattdessen herunterladen
            </button>
          </div>
        )}

        {!loading && !error && blobUrl && canPreview && (
          <>
            {isPdf && (
              <iframe
                src={blobUrl}
                title={doc.title}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            )}
            {isImage && (
              <div style={{ width: '100%', height: '100%', overflow: 'auto',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: 16, boxSizing: 'border-box', background: 'var(--bg-deep)' }}>
                <img src={blobUrl} alt={doc.title}
                  style={{ maxWidth: '100%', borderRadius: 6, boxShadow: '0 4px 20px #0006' }} />
              </div>
            )}
            {isHtml && (
              <iframe
                src={blobUrl}
                title={doc.title}
                sandbox="allow-same-origin"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block',
                  background: '#fff' }}
              />
            )}
          </>
        )}

        {!loading && !error && blobUrl && !canPreview && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 32,
            textAlign: 'center', color: 'var(--text2)' }}>
            <div style={{ fontSize: 52 }}>{fileIcon(ct)}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 6 }}>
                Keine Direktvorschau verfügbar
              </div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.5 }}>
                {ct.includes('word') || ct.includes('document')
                  ? 'Word-Dokumente können nicht direkt angezeigt werden.'
                  : ct.includes('excel') || ct.includes('sheet')
                  ? 'Excel-Dateien können nicht direkt angezeigt werden.'
                  : 'Dieser Dateityp kann nicht im Browser angezeigt werden.'}
                <br />Bitte herunterladen, um den Inhalt zu sehen.
              </div>
            </div>
            <button onClick={() => onDownload(doc)}
              style={{ padding: '10px 20px', background: 'var(--accent)', color: '#000',
                border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
              ⬇ Herunterladen
            </button>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {doc.original_name} · {fmt_size(doc.file_size)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DocumentsPage() {
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [category, setCat]      = useState('all');
  const [search, setSearch]     = useState('');
  const [showUpload, setUpload] = useState(false);
  const [editDoc, setEdit]      = useState(null);
  const [previewDoc, setPreview]= useState(null);
  const [err, setErr]           = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (search)             params.q = search;
      const r = await API.get('/documents', { params });
      setDocs(r.data);
    } catch (e) {
      setErr('Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, search]);

  const download = (doc) => {
    const token = JSON.parse(localStorage.getItem('energy_auth') || 'null')?.access_token || '';
    fetch(`/api/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = doc.original_name;
        a.click(); URL.revokeObjectURL(url);
      });
  };

  const del = async (doc) => {
    if (!window.confirm(`"${doc.title}" wirklich löschen?`)) return;
    try {
      await API.delete(`/documents/${doc.id}`);
      if (previewDoc?.id === doc.id) setPreview(null);
      setDocs(d => d.filter(x => x.id !== doc.id));
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const counts = docs.reduce((acc, d) => { acc[d.category] = (acc[d.category] || 0) + 1; return acc; }, {});
  const totalCount = docs.length;
  const showSplit = !!previewDoc;

  return (
    <div className="page-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {showUpload && <UploadModal onClose={() => setUpload(false)} onUploaded={load} />}
      {editDoc   && <EditModal doc={editDoc} onClose={() => setEdit(null)} onSaved={() => { load(); if (previewDoc?.id === editDoc.id) setPreview(p => p ? { ...p, ...editDoc } : p); }} />}

      {/* Page header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1>📁 Planungsdokumente</h1>
            <p>Zentrale Dokumentenbibliothek für die Planungsphase</p>
          </div>
          <button className="btn btn-primary" onClick={() => setUpload(true)}>
            📤 Hochladen
          </button>
        </div>
      </div>

      {err && <div className="error-msg" onClick={() => setErr('')}>{err}</div>}

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap',
        borderBottom: '1px solid var(--border)', marginBottom: 16, flexShrink: 0 }}>
        {CATEGORIES.map(c => {
          const cnt = c.id === 'all' ? totalCount : (counts[c.id] || 0);
          return (
            <button key={c.id} onClick={() => setCat(c.id)}
              className="btn btn-ghost btn-sm"
              style={{
                borderBottom: category === c.id ? `2px solid ${c.color}` : '2px solid transparent',
                borderRadius: '6px 6px 0 0',
                color: category === c.id ? c.color : 'var(--text2)',
                fontWeight: category === c.id ? 700 : 400,
              }}>
              {c.icon} {c.label}
              {cnt > 0 && (
                <span style={{ marginLeft: 5, fontSize: 10, padding: '1px 5px', borderRadius: 10,
                  background: category === c.id ? c.color + '33' : 'var(--bg-hover)',
                  color: category === c.id ? c.color : 'var(--text3)', fontWeight: 700 }}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 14, flexShrink: 0 }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Suche nach Titel, Beschreibung, Dateiname…"
          style={{ width: '100%', maxWidth: showSplit ? '100%' : 420, padding: '8px 12px',
            borderRadius: 7, background: 'var(--bg-surface)', border: '1px solid var(--border)',
            color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' }}
        />
      </div>

      {/* Main content: split or full */}
      <div style={{ flex: 1, display: 'flex', gap: 14, minHeight: 0, overflow: 'hidden' }}>

        {/* Document list */}
        <div style={{
          width: showSplit ? '38%' : '100%',
          flexShrink: 0,
          overflowY: 'auto',
          transition: 'width 0.2s ease',
        }}>
          {loading && <div className="loading">Lade…</div>}

          {!loading && docs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text3)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Keine Dokumente</div>
              <div style={{ fontSize: 13, marginBottom: 20 }}>
                {search ? 'Keine Ergebnisse für deine Suche.' : 'Noch keine Dokumente hochgeladen.'}
              </div>
              {!search && (
                <button className="btn btn-primary" onClick={() => setUpload(true)}>
                  📤 Erstes Dokument hochladen
                </button>
              )}
            </div>
          )}

          {!loading && docs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {docs.map(doc => {
                const isActive = previewDoc?.id === doc.id;
                return (
                  <div key={doc.id}
                    onClick={() => setPreview(isActive ? null : doc)}
                    style={{
                      background: isActive ? 'var(--accent-glow, #3b82f610)' : 'var(--bg-card)',
                      border: `1px solid ${isActive ? 'var(--accent, #3b82f6)' : 'var(--border)'}`,
                      borderRadius: 8,
                      padding: showSplit ? '10px 12px' : '12px 16px',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--accent, #3b82f6)40'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      {/* File icon */}
                      <div style={{ fontSize: showSplit ? 22 : 26, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>
                        {fileIcon(doc.content_type)}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                          flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontWeight: 700, fontSize: showSplit ? 13 : 14,
                            color: isActive ? 'var(--accent, #3b82f6)' : 'var(--text1)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: showSplit ? 160 : 'none' }}>
                            {doc.title}
                          </span>
                          {!showSplit && <CatBadge category={doc.category} />}
                        </div>
                        {showSplit ? (
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {catMeta(doc.category).icon} {catMeta(doc.category).label}
                            {' · '}{fmt_size(doc.file_size)}
                          </div>
                        ) : (
                          <>
                            {doc.description && (
                              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 3,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {doc.description}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap' }}>
                              <span>{doc.original_name}</span>
                              <span>{fmt_size(doc.file_size)}</span>
                              <span>📅 {fmt_date(doc.created_at)}</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Actions — stop propagation so click on doc card doesn't toggle preview */}
                      {!showSplit && (
                        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}
                          onClick={e => e.stopPropagation()}>
                          <button onClick={() => download(doc)}
                            style={{ padding: '4px 9px', background: '#3b82f620', color: '#3b82f6',
                              border: '1px solid #3b82f640', borderRadius: 5, cursor: 'pointer',
                              fontSize: 11, fontWeight: 600 }}>
                            ⬇
                          </button>
                          <button onClick={() => setEdit(doc)}
                            style={{ padding: '4px 8px', background: 'var(--bg-surface)',
                              color: 'var(--text2)', border: '1px solid var(--border)',
                              borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>
                            ✏️
                          </button>
                          <button onClick={() => del(doc)}
                            style={{ padding: '4px 8px', background: '#ef444415', color: '#ef4444',
                              border: '1px solid #ef444430', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>
                            🗑
                          </button>
                        </div>
                      )}

                      {/* Compact arrow when in split mode */}
                      {showSplit && (
                        <div style={{ fontSize: 14, color: isActive ? 'var(--accent, #3b82f6)' : 'var(--text3)',
                          flexShrink: 0, alignSelf: 'center', marginLeft: 4 }}>
                          {isActive ? '▶' : '›'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', marginTop: 4, paddingRight: 4 }}>
                {docs.length} Dokument{docs.length !== 1 ? 'e' : ''}
              </div>
            </div>
          )}
        </div>

        {/* Preview panel */}
        {showSplit && (
          <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <PreviewPanel
              doc={previewDoc}
              onClose={() => setPreview(null)}
              onDownload={download}
              onEdit={(doc) => { setEdit(doc); }}
              onDelete={del}
            />
          </div>
        )}
      </div>
    </div>
  );
}
